/**
 * IndexedDB обертка для хранения canvas workspace данных
 * Заменяет localStorage для преодоления ограничений по размеру
 */

const DB_NAME = 'canvas-workspace-db';
const DB_VERSION = 1;
const STORE_NAME = 'workspaces';

export interface PersistentStorage {
  workspaces: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [workspaceId: string]: Record<string, any>;
  };
}

class CanvasIndexedDB {
  private dbPromise: Promise<IDBDatabase> | null = null;

  /**
   * Инициализирует подключение к IndexedDB
   */
  private getDB(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('❌ [IndexedDB] Failed to open database:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        console.debug('✅ [IndexedDB] Database opened successfully');
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Создаем object store если его нет
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          console.debug('✅ [IndexedDB] Object store created:', STORE_NAME);
        }
      };
    });

    return this.dbPromise;
  }

  /**
   * Получает данные из IndexedDB
   */
  async getItem(key: string): Promise<string | null> {
    try {
      const db = await this.getDB();
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const result = request.result;
          resolve(result ? result.value : null);
        };
        request.onerror = () => {
          console.error('❌ [IndexedDB] Error reading data:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('❌ [IndexedDB] getItem error:', error);
      return null;
    }
  }

  /**
   * Сохраняет данные в IndexedDB
   */
  async setItem(key: string, value: string): Promise<void> {
    try {
      const db = await this.getDB();
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put({ id: key, value });

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          resolve();
        };
        request.onerror = () => {
          console.error('❌ [IndexedDB] Error saving data:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('❌ [IndexedDB] setItem error:', error);
      throw error;
    }
  }

  /**
   * Удаляет элемент из IndexedDB
   */
  async removeItem(key: string): Promise<void> {
    try {
      const db = await this.getDB();
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(key);

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          console.debug('✅ [IndexedDB] Item removed:', key);
          resolve();
        };
        request.onerror = () => {
          console.error('❌ [IndexedDB] Error removing data:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('❌ [IndexedDB] removeItem error:', error);
      throw error;
    }
  }

  /**
   * Очищает все данные из IndexedDB
   */
  async clear(): Promise<void> {
    try {
      const db = await this.getDB();
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          console.debug('✅ [IndexedDB] All data cleared');
          resolve();
        };
        request.onerror = () => {
          console.error('❌ [IndexedDB] Error clearing data:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('❌ [IndexedDB] clear error:', error);
      throw error;
    }
  }

  /**
   * Закрывает соединение с базой данных
   */
  close(): void {
    if (this.dbPromise) {
      this.dbPromise
        .then((db) => db.close())
        .catch(() => {
          /* ignore */
        });
      this.dbPromise = null;
    }
  }
}

// Singleton экземпляр
export const canvasIndexedDB = new CanvasIndexedDB();
